/**
 * TNWS shop filters — whole-catalog, tag-driven filtering for collection + search grids.
 *
 * The server renders one page of cards. In the background every page of the same
 * section is fetched through the Section Rendering API, so filters, counts,
 * per-page and sorting work across the full result set without a reload.
 * Card markup always comes from Liquid (card-product), never rebuilt in JS.
 */
(() => {
  const PER_PAGE_KEY = 'tnws:per-page';
  const PARAM_PREFIX = 'tf_';
  const VALUE_SEP = '|';

  const PRICE_PRESETS = [
    { id: '0-15', label: 'Under $15', min: 0, max: 1500 },
    { id: '15-25', label: '$15 – $25', min: 1500, max: 2500 },
    { id: '25-50', label: '$25 – $50', min: 2500, max: 5000 },
    { id: '50-100', label: '$50 – $100', min: 5000, max: 10000 },
    { id: '100-250', label: '$100 – $250', min: 10000, max: 25000 },
    { id: '250-', label: '$250 & up', min: 25000, max: null },
  ];

  const DIAL_BUCKETS = [
    { label: 'Under 30mm', test: (n) => n < 30 },
    { label: '30 – 35mm', test: (n) => n >= 30 && n < 36 },
    { label: '36 – 40mm', test: (n) => n >= 36 && n < 41 },
    { label: '41 – 44mm', test: (n) => n >= 41 && n < 45 },
    { label: '45mm & up', test: (n) => n >= 45 },
  ];

  const THICKNESS_BUCKETS = [
    { label: 'Slim (under 8mm)', test: (n) => n < 8 },
    { label: 'Classic (8 – 10mm)', test: (n) => n >= 8 && n <= 10 },
    { label: 'Bold (11mm & up)', test: (n) => n > 10 },
  ];

  const WRIST_PRESETS = [6, 6.5, 7, 7.5, 8];
  const WRIST_STEP = 0.25;
  const WRIST_MIN = 4;
  const WRIST_MAX = 10;
  // Anything typed above this is read as centimeters (adult wrists are never 12+ inches).
  const WRIST_CM_THRESHOLD = 12;

  const formatInches = (value) => `${Number(value.toFixed(2))} in`;
  const formatCm = (inches) => `${Number((inches * 2.54).toFixed(1))} cm`;
  const formatWrist = (inches) => `${formatInches(inches)} · ${formatCm(inches)}`;

  const CONDITION_ORDER = ['New stock', 'Excellent refurbished', 'Good refurbished', 'Fair refurbished', 'Poor refurbished'];
  const GENDER_ORDER = ["Men's", "Women's", 'Unisex'];

  const STYLE_TAGS = new Map([
    ['modern', 'Modern'],
    ['vintage', 'Vintage'],
    ['sports', 'Sport'],
    ['sport', 'Sport'],
    ['tank style', 'Tank'],
    ['dress', 'Dress'],
    ['diver', 'Diver'],
    ['collector', 'Collector'],
    ['rare', 'Rare'],
    ['limited edition', 'Limited edition'],
    ['limited quantity', 'Limited quantity'],
    ['precious metals', 'Precious metals'],
  ]);

  const MOVEMENT_TAGS = new Map([
    ['quartz', 'Quartz'],
    ['quartz movement', 'Quartz'],
    ['automatic', 'Automatic'],
    ['automatic movement', 'Automatic'],
    ['manual', 'Manual wind'],
    ['manual winding', 'Manual wind'],
    ['manual wind', 'Manual wind'],
    ['solar', 'Solar'],
    ['solar powered', 'Solar'],
    ['digital', 'Digital'],
  ]);

  const FACETS = [
    { key: 'brand', label: 'Brand', primary: true, searchable: true, sort: 'alpha' },
    { key: 'price', label: 'Price', primary: true, type: 'price' },
    { key: 'wrist', label: 'Wrist size', primary: true, type: 'wrist' },
    { key: 'gender', label: 'Gender', primary: true, order: GENDER_ORDER },
    { key: 'condition', label: 'Condition', primary: true, order: CONDITION_ORDER },
    { key: 'dial', label: 'Dial size', primary: true, order: DIAL_BUCKETS.map((b) => b.label) },
    { key: 'color', label: 'Color', primary: true, sort: 'alpha', swatches: true },
    { key: 'band', label: 'Band type', sort: 'count' },
    { key: 'bandwidth', label: 'Band width', sort: 'numeric' },
    { key: 'thickness', label: 'Thickness', order: THICKNESS_BUCKETS.map((b) => b.label) },
    { key: 'movement', label: 'Movement', sort: 'count' },
    { key: 'style', label: 'Style', sort: 'count' },
    { key: 'features', label: 'Features', sort: 'count' },
    { key: 'material', label: 'Material', sort: 'alpha' },
    { key: 'tag', label: 'More tags', searchable: true, sort: 'count' },
  ];

  const SWATCHES = {
    black: '#151515', white: '#ffffff', silver: '#c9ccd1', gold: '#d4af37', 'rose gold': '#d9a08b',
    blue: '#2f5aa8', navy: '#24304a', brown: '#7a5232', bronze: '#a0703c', copper: '#b87333',
    gray: '#8a8d93', grey: '#8a8d93', green: '#3f7d4e', red: '#b8312f', pink: '#e8a3b8',
    purple: '#6e4a9e', orange: '#e37b2c', yellow: '#e8c547', beige: '#d9c7a7', clear: 'transparent',
  };

  const titleCase = (value) =>
    String(value)
      .split(' ')
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(' ');

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (cents) => {
    const dollars = cents / 100;
    return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
  };

  class TagTaxonomy {
    constructor() {
      const groups = window.TNWS_TAG_GROUPS?.groups || {};
      const lookup = (keys) => {
        const map = new Map();
        keys.forEach((key) => (groups[key] || []).forEach((tag) => map.set(String(tag).trim().toLowerCase(), String(tag).trim())));
        return map;
      };
      this.brands = lookup(['brand']);
      this.colors = lookup(['color']);
      this.bandTypes = lookup(['band_type']);
      this.features = lookup(['watch_features', 'watch_feature_inputs']);
      this.brandsByLength = [...this.brands.entries()].sort((a, b) => b[0].length - a[0].length);
    }

    classify(rawTag) {
      const tag = rawTag.trim().toLowerCase();
      if (!tag || tag.startsWith('_')) return null;

      let match = tag.match(/^(\d+(?:\.\d+)?)\s*mm\s*(dial|case)$/);
      if (match) {
        const size = Number(match[1]);
        const bucket = DIAL_BUCKETS.find((b) => b.test(size));
        return bucket ? ['dial', bucket.label] : null;
      }
      match = tag.match(/^(\d+(?:\.\d+)?)\s*mm\s*(band|strap|lug)$/);
      if (match) return ['bandwidth', `${match[1]}mm`];
      match = tag.match(/^(\d+(?:\.\d+)?)\s*mm\s*thick(ness)?$/);
      if (match) {
        const size = Number(match[1]);
        const bucket = THICKNESS_BUCKETS.find((b) => b.test(size));
        return bucket ? ['thickness', bucket.label] : null;
      }

      if (/^(mens|men's|men|male|gents)$/.test(tag)) return ['gender', "Men's"];
      if (/^(womens|women's|women|ladies|female)$/.test(tag)) return ['gender', "Women's"];
      if (tag === 'unisex') return ['gender', 'Unisex'];

      if (tag === 'new stock' || tag === 'new') return ['condition', 'New stock'];
      match = tag.match(/^(excellent|good|fair|poor)\s+refurbish(ed)?$/);
      if (match) return ['condition', `${titleCase(match[1])} refurbished`];

      if (tag === 'sizeband' || tag === 'sizeable band') return ['features', 'Band can be sized'];
      if (this.brands.has(tag)) return ['brand', this.brands.get(tag)];
      if (this.colors.has(tag)) return ['color', titleCase(this.colors.get(tag))];
      if (MOVEMENT_TAGS.has(tag)) return ['movement', MOVEMENT_TAGS.get(tag)];
      if (STYLE_TAGS.has(tag)) return ['style', STYLE_TAGS.get(tag)];
      if (this.bandTypes.has(tag) || /(bracelet|strap|bangle)$/.test(tag) || tag === 'integrated metal band') {
        return ['band', titleCase(tag)];
      }
      if (/(gold plate|plated|sterling|stainless|titanium|ceramic|925|14k|18k|10k)/.test(tag)) {
        return ['material', titleCase(tag)];
      }
      if (this.features.has(tag)) return ['features', titleCase(this.features.get(tag))];

      return ['tag', titleCase(tag)];
    }

    describe(item) {
      const values = {};
      const add = (key, value) => {
        if (!value) return;
        (values[key] ||= new Set()).add(value);
      };

      item.tags.forEach((tag) => {
        const result = this.classify(tag);
        if (result) add(result[0], result[1]);
      });

      const title = item.title.toLowerCase();
      if (!values.brand) {
        const found = this.brandsByLength.find(([lower]) => title === lower || title.startsWith(`${lower} `));
        if (found) add('brand', found[1]);
      }
      if (!values.gender) {
        if (/\b(men'?s|gents)\b/.test(title)) add('gender', "Men's");
        if (/\b(women'?s|ladies)\b/.test(title)) add('gender', "Women's");
      }
      if (/\bdigital\b/.test(title)) add('movement', 'Digital');
      if (/\bautomatic\b/.test(title)) add('movement', 'Automatic');
      if (/\bmanual wind/.test(title)) add('movement', 'Manual wind');
      if (/\bchrono/.test(title)) add('features', 'Chronograph look');
      if (/\bdate\b/.test(title)) add('features', 'Date display');
      if (/\bdiver\b/.test(title)) add('style', 'Diver');

      return values;
    }
  }

  class TnwsShopFilters {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-tnws-shop-grid]');
      this.toolbar = root.querySelector('[data-tnws-shop-toolbar]');
      if (!this.grid || !this.toolbar) return;

      this.sectionId = root.dataset.sectionId;
      this.pageCount = Number(root.dataset.pages || 1);
      this.serverPage = Number(root.dataset.currentPage || 1);
      this.serverPerPage = Number(root.dataset.perPage || 24);
      this.total = Number(root.dataset.total || 0);
      this.noun = root.dataset.noun || 'watches';

      this.taxonomy = new TagTaxonomy();
      this.selected = new Map(FACETS.map((facet) => [facet.key, new Set()]));
      this.price = { preset: '', min: null, max: null };
      this.wrist = null;
      this.rulerUrl = this.toolbar.dataset.tnwsRulerUrl || '';
      this.query = '';
      this.page = 1;
      this.pool = null;
      this.clientMode = false;
      this.openFacets = new Set(['brand', 'price']);

      this.pills = this.toolbar.querySelector('[data-tnws-shop-pills]');
      this.chips = this.toolbar.querySelector('[data-tnws-shop-chips]');
      this.countEl = this.toolbar.querySelector('[data-tnws-shop-count]');
      this.badge = this.toolbar.querySelector('[data-tnws-shop-badge]');
      this.perPageSelect = this.toolbar.querySelector('[data-tnws-shop-per-page]');
      this.sortSelect = this.toolbar.querySelector('[data-tnws-shop-sort]');
      this.popover = root.querySelector('[data-tnws-shop-popover]');
      this.drawer = root.querySelector('[data-tnws-shop-drawer]');
      this.facetsEl = this.drawer?.querySelector('[data-tnws-shop-facets]');
      this.queryInput = this.drawer?.querySelector('[data-tnws-shop-query]');
      this.applyButton = this.drawer?.querySelector('[data-tnws-shop-apply]');
      this.emptyState = root.querySelector('[data-tnws-shop-empty]');
      this.nativePagination = root.querySelector('[data-tnws-shop-native-pagination]');

      if (this.drawer) document.body.appendChild(this.drawer);
      if (this.popover) document.body.appendChild(this.popover);

      this.pagesEl = document.createElement('div');
      this.pagesEl.className = 'tnws-shop-pages';
      this.pagesEl.hidden = true;
      this.grid.insertAdjacentElement('afterend', this.pagesEl);

      this.perPage = this.readStoredPerPage();
      this.readUrlState();
      this.syncPerPageSelect();
      this.bind();
      this.renderPills();
      this.load();
    }

    /* ---------- state ---------- */

    readStoredPerPage() {
      try {
        const stored = localStorage.getItem(PER_PAGE_KEY);
        if (stored === 'all') return Infinity;
        const n = Number(stored);
        if (n > 0) return n;
      } catch (e) {
        /* storage unavailable */
      }
      return this.serverPerPage;
    }

    storePerPage(value) {
      try {
        if (value === this.serverPerPage) localStorage.removeItem(PER_PAGE_KEY);
        else localStorage.setItem(PER_PAGE_KEY, value === Infinity ? 'all' : String(value));
      } catch (e) {
        /* storage unavailable */
      }
    }

    syncPerPageSelect() {
      if (!this.perPageSelect) return;
      const value = this.perPage === Infinity ? 'all' : String(this.perPage);
      if (![...this.perPageSelect.options].some((o) => o.value === value)) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        this.perPageSelect.prepend(option);
      }
      this.perPageSelect.value = value;
    }

    readUrlState() {
      const params = new URLSearchParams(window.location.search);
      FACETS.forEach((facet) => {
        if (facet.type) return;
        const raw = params.get(PARAM_PREFIX + facet.key);
        if (raw) raw.split(VALUE_SEP).filter(Boolean).forEach((v) => this.selected.get(facet.key).add(v));
      });
      const price = params.get(`${PARAM_PREFIX}price`);
      if (price) {
        const preset = PRICE_PRESETS.find((p) => p.id === price);
        if (preset) this.price = { preset: preset.id, min: preset.min, max: preset.max };
        else {
          const [min, max] = price.split('-').map((v) => (v === '' ? null : Math.round(Number(v) * 100)));
          this.price = { preset: '', min: Number.isFinite(min) ? min : null, max: Number.isFinite(max) ? max : null };
        }
      }
      this.setWrist(params.get(`${PARAM_PREFIX}wrist`));
      this.query = (params.get(`${PARAM_PREFIX}q`) || '').trim();
      if (this.queryInput) this.queryInput.value = this.query;
      const page = Number(params.get(`${PARAM_PREFIX}page`));
      this.page = page > 0 ? page : 1;
      this.urlHadState = this.hasActiveFilters() || params.has(`${PARAM_PREFIX}page`);
    }

    writeUrlState() {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].filter((k) => k.startsWith(PARAM_PREFIX)).forEach((k) => url.searchParams.delete(k));
      url.searchParams.delete('page');
      FACETS.forEach((facet) => {
        const set = this.selected.get(facet.key);
        if (set?.size) url.searchParams.set(PARAM_PREFIX + facet.key, [...set].join(VALUE_SEP));
      });
      if (this.price.preset) url.searchParams.set(`${PARAM_PREFIX}price`, this.price.preset);
      else if (this.price.min != null || this.price.max != null) {
        const fmt = (v) => (v == null ? '' : String(v / 100));
        url.searchParams.set(`${PARAM_PREFIX}price`, `${fmt(this.price.min)}-${fmt(this.price.max)}`);
      }
      if (this.wrist != null) url.searchParams.set(`${PARAM_PREFIX}wrist`, String(this.wrist));
      if (this.query) url.searchParams.set(`${PARAM_PREFIX}q`, this.query);
      if (this.page > 1) url.searchParams.set(`${PARAM_PREFIX}page`, String(this.page));
      window.history.replaceState(window.history.state, '', url.toString());
    }

    hasActiveFilters() {
      return this.activeCount() > 0;
    }

    activeCount() {
      let count = 0;
      this.selected.forEach((set) => (count += set.size));
      if (this.price.preset || this.price.min != null || this.price.max != null) count += 1;
      if (this.wrist != null) count += 1;
      if (this.query) count += 1;
      return count;
    }

    /** Accepts inches or centimeters; returns false when the value can't be used. */
    setWrist(raw) {
      const text = String(raw ?? '').trim().replace(',', '.');
      if (!text) {
        this.wrist = null;
        return true;
      }
      let value = parseFloat(text);
      if (!Number.isFinite(value) || value <= 0) return false;
      if (/cm/i.test(text) || value > WRIST_CM_THRESHOLD) value /= 2.54;
      if (value < WRIST_MIN || value > WRIST_MAX) return false;
      this.wrist = Math.round(value * 100) / 100;
      return true;
    }

    /* ---------- data ---------- */

    itemFromElement(el) {
      const isProduct = el.hasAttribute('data-tnws-catalog-item');
      const tags = (el.dataset.tnwsTags || '').split('||').map((t) => t.trim()).filter(Boolean);
      const item = {
        el,
        isProduct,
        handle: el.dataset.productHandle || '',
        title: el.dataset.tnwsTitle || '',
        price: Number(el.dataset.tnwsPrice || 0),
        maxWrist: parseFloat(el.dataset.tnwsMaxWrist) || null,
        tags,
      };
      item.values = isProduct ? this.taxonomy.describe(item) : {};
      item.haystack = `${item.title} ${tags.join(' ')}`.toLowerCase();
      return item;
    }

    prepareElement(el) {
      el.hidden = false;
      el.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      el.querySelectorAll('.scroll-trigger').forEach((child) => child.classList.remove('scroll-trigger', 'scroll-trigger--offscreen'));
      return el;
    }

    sectionUrl(page, sortBy) {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].filter((k) => k.startsWith(PARAM_PREFIX)).forEach((k) => url.searchParams.delete(k));
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', String(page));
      if (sortBy) url.searchParams.set('sort_by', sortBy);
      return url.toString();
    }

    async fetchPage(page, sortBy) {
      const response = await fetch(this.sectionUrl(page, sortBy), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`page ${page}: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const pages = Number(doc.querySelector('[data-tnws-shop]')?.dataset.pages || this.pageCount);
      const items = [...doc.querySelectorAll('[data-tnws-shop-grid] > li')];
      return { pages, items };
    }

    async fetchPool(sortBy = null) {
      const reuse = new Map();
      (this.pool || [...this.grid.children].map((el) => this.itemFromElement(el))).forEach((item) => {
        if (item.handle) reuse.set(item.handle, item.el);
      });

      const first = await this.fetchPage(1, sortBy);
      const pages = [first];
      if (first.pages > 1) {
        const rest = await Promise.all(
          Array.from({ length: first.pages - 1 }, (_, i) => this.fetchPage(i + 2, sortBy))
        );
        pages.push(...rest);
      }
      this.pageCount = first.pages;

      const seen = new Set();
      const pool = [];
      pages.forEach(({ items }) => {
        items.forEach((fetched) => {
          const handle = fetched.dataset.productHandle;
          if (handle && seen.has(handle)) return;
          if (handle) seen.add(handle);
          const el = handle && reuse.has(handle) ? reuse.get(handle) : document.importNode(fetched, true);
          if (handle && reuse.has(handle)) {
            ['tnwsTags', 'tnwsTitle', 'tnwsPrice', 'tnwsMaxWrist'].forEach((k) => {
              if (fetched.dataset[k] != null) el.dataset[k] = fetched.dataset[k];
            });
          }
          pool.push(this.itemFromElement(this.prepareElement(el)));
        });
      });
      return pool;
    }

    async load() {
      this.root.classList.add('is-tnws-loading-pool');
      if (this.urlHadState || this.perPage !== this.serverPerPage) this.grid.classList.add('is-tnws-busy');
      try {
        this.pool = await this.fetchPool();
      } catch (error) {
        console.warn('[tnws-shop-filters] could not load full catalog; filtering this page only', error);
        this.pool = [...this.grid.children].map((el) => this.itemFromElement(el));
      }
      this.root.classList.remove('is-tnws-loading-pool');
      this.grid.classList.remove('is-tnws-busy');

      this.renderFacets();
      this.renderPills();
      if (this.urlHadState || this.perPage !== this.serverPerPage) {
        if (!this.urlHadState && this.perPage !== this.serverPerPage) {
          const firstIndex = (this.serverPage - 1) * this.serverPerPage;
          this.page = this.perPage === Infinity ? 1 : Math.floor(firstIndex / this.perPage) + 1;
        }
        this.render({ scroll: false });
      } else {
        this.updateCount(this.total);
        this.renderChips();
      }
    }

    /* ---------- matching ---------- */

    matchesPrice(item) {
      const { min, max } = this.price;
      if (min != null && item.price < min) return false;
      if (max != null && item.price > max) return false;
      return true;
    }

    fitsWrist(item, wrist) {
      if (wrist == null) return true;
      // ~1mm of slack so centimeter rounding (17.8 cm = 7.01 in) doesn't drop 7 in bands.
      return item.maxWrist != null && item.maxWrist + 0.05 >= wrist;
    }

    matches(item, skipKey = null) {
      if (!item.isProduct) return !this.hasActiveFilters();
      if (this.query) {
        const words = this.query.toLowerCase().split(/\s+/).filter(Boolean);
        if (!words.every((w) => item.haystack.includes(w))) return false;
      }
      if (skipKey !== 'price' && !this.matchesPrice(item)) return false;
      if (skipKey !== 'wrist' && !this.fitsWrist(item, this.wrist)) return false;
      for (const [key, set] of this.selected) {
        if (!set.size || key === skipKey) continue;
        const values = item.values[key];
        if (!values || ![...set].some((v) => values.has(v))) return false;
      }
      return true;
    }

    filtered() {
      return (this.pool || []).filter((item) => this.matches(item));
    }

    facetOptions(facet) {
      const counts = new Map();
      (this.pool || []).forEach((item) => {
        if (!item.isProduct || !this.matches(item, facet.key)) return;
        (item.values[facet.key] || []).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
      });
      this.selected.get(facet.key)?.forEach((value) => {
        if (!counts.has(value)) counts.set(value, 0);
      });

      let options = [...counts.entries()].map(([value, count]) => ({ value, count }));
      if (facet.order) {
        options.sort((a, b) => facet.order.indexOf(a.value) - facet.order.indexOf(b.value));
      } else if (facet.sort === 'numeric') {
        options.sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
      } else if (facet.sort === 'count') {
        options.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
      } else {
        options.sort((a, b) => a.value.localeCompare(b.value));
      }
      return options;
    }

    priceOptions() {
      return PRICE_PRESETS.map((preset) => {
        let count = 0;
        (this.pool || []).forEach((item) => {
          if (!item.isProduct || !this.matches(item, 'price')) return;
          if (item.price >= preset.min && (preset.max == null || item.price < preset.max)) count += 1;
        });
        return { ...preset, count };
      });
    }

    /* ---------- rendering ---------- */

    optionMarkup(facet, option) {
      const checked = this.selected.get(facet.key).has(option.value);
      const swatch = facet.swatches ? SWATCHES[option.value.toLowerCase()] : null;
      const swatchHtml = facet.swatches
        ? `<span class="tnws-shop-option__swatch${swatch === 'transparent' ? ' is-clear' : ''}" style="background:${swatch || '#ddd'}"></span>`
        : '';
      return `
        <label class="tnws-shop-option${option.count === 0 && !checked ? ' is-empty' : ''}">
          <input type="checkbox" data-tnws-shop-facet="${facet.key}" value="${escapeHtml(option.value)}"${checked ? ' checked' : ''}>
          <span class="tnws-shop-option__box" aria-hidden="true"></span>
          ${swatchHtml}
          <span class="tnws-shop-option__label">${escapeHtml(option.value)}</span>
          <span class="tnws-shop-option__count">${option.count}</span>
        </label>`;
    }

    priceMarkup() {
      const options = this.priceOptions()
        .filter((o) => o.count > 0 || this.price.preset === o.id)
        .map(
          (o) => `
        <label class="tnws-shop-option">
          <input type="radio" name="tnws-shop-price-${this.sectionId}" data-tnws-shop-price-preset value="${o.id}"${this.price.preset === o.id ? ' checked' : ''}>
          <span class="tnws-shop-option__box tnws-shop-option__box--radio" aria-hidden="true"></span>
          <span class="tnws-shop-option__label">${o.label}</span>
          <span class="tnws-shop-option__count">${o.count}</span>
        </label>`
        )
        .join('');
      const custom = !this.price.preset;
      const fmt = (v) => (custom && v != null ? v / 100 : '');
      return `
        <div class="tnws-shop-options">${options}</div>
        <div class="tnws-shop-price-range">
          <label><span>Min $</span><input type="number" min="0" inputmode="decimal" data-tnws-shop-price-min value="${fmt(this.price.min)}" placeholder="No min"></label>
          <span class="tnws-shop-price-range__sep" aria-hidden="true">–</span>
          <label><span>Max $</span><input type="number" min="0" inputmode="decimal" data-tnws-shop-price-max value="${fmt(this.price.max)}" placeholder="No max"></label>
        </div>`;
    }

    wristCount(wrist) {
      let count = 0;
      (this.pool || []).forEach((item) => {
        if (item.isProduct && this.matches(item, 'wrist') && this.fitsWrist(item, wrist)) count += 1;
      });
      return count;
    }

    wristMarkup() {
      const inches = this.wrist != null ? Number(this.wrist.toFixed(2)) : '';
      const cm = this.wrist != null ? Number((this.wrist * 2.54).toFixed(1)) : '';
      const presets = WRIST_PRESETS.map((size) => {
        const active = this.wrist === size;
        return `<button type="button" class="tnws-shop-wrist__preset${active ? ' is-active' : ''}" data-tnws-shop-wrist-preset="${size}" aria-pressed="${active}">
            <span class="tnws-shop-wrist__preset-size">${size} in <span class="tnws-shop-wrist__preset-cm">${formatCm(size)}</span></span>
            <span class="tnws-shop-wrist__preset-count">${this.wristCount(size)}</span>
          </button>`;
      }).join('');
      const status =
        this.wrist != null
          ? `${this.wristCount(this.wrist)} fit a ${formatWrist(this.wrist)} wrist`
          : 'Enter your wrist size in inches or centimeters.';
      const field = (attr, unit, value, placeholder, label) => `
            <label class="tnws-shop-wrist__field">
              <span class="visually-hidden">${label}</span>
              <input type="text" inputmode="decimal" autocomplete="off" class="tnws-shop-wrist__input" ${attr} value="${value}" placeholder="${placeholder}" aria-describedby="tnws-shop-wrist-status-${this.sectionId}">
              <span class="tnws-shop-wrist__unit" aria-hidden="true">${unit}</span>
            </label>`;
      const ruler = this.rulerUrl
        ? ` No ruler? <a href="${escapeHtml(this.rulerUrl)}" target="_blank" rel="noopener noreferrer" class="tnws-shop-wrist__ruler">Print our wrist ruler (PDF)</a>`
        : '';
      return `
        <div class="tnws-shop-wrist">
          <div class="tnws-shop-wrist__control">
            <button type="button" class="tnws-shop-wrist__step" data-tnws-shop-wrist-step="-1" aria-label="Smaller wrist size">−</button>
            ${field('data-tnws-shop-wrist-input', 'in', inches, '6.5', 'Wrist size in inches')}
            <span class="tnws-shop-wrist__eq" aria-hidden="true">=</span>
            ${field('data-tnws-shop-wrist-cm', 'cm', cm, '16.5', 'Wrist size in centimeters')}
            <button type="button" class="tnws-shop-wrist__step" data-tnws-shop-wrist-step="1" aria-label="Larger wrist size">+</button>
          </div>
          <p class="tnws-shop-wrist__status" id="tnws-shop-wrist-status-${this.sectionId}" aria-live="polite">${status}</p>
          <div class="tnws-shop-wrist__presets">${presets}</div>
          <p class="tnws-shop-wrist__hint">Measure around your wrist where you wear a watch.${ruler}</p>
        </div>`;
    }

    applyWrist(raw, focusSelector = null) {
      const ok = this.setWrist(raw);
      const scope = this.popoverKey === 'wrist' ? this.popover : this.facetsEl;
      if (!ok) {
        const status = scope?.querySelector('.tnws-shop-wrist__status');
        if (status) status.textContent = `Enter a wrist size between ${WRIST_MIN} and ${WRIST_MAX} in (or in cm).`;
        scope?.querySelector('.tnws-shop-wrist')?.classList.add('is-invalid');
        return;
      }
      this.onChange({ scroll: false });
      if (focusSelector) {
        const target = (this.popoverKey === 'wrist' ? this.popover : this.facetsEl)?.querySelector(focusSelector);
        target?.focus({ preventScroll: true });
      }
    }

    stepWrist(direction, focusSelector) {
      const base = this.wrist ?? 6.5;
      let next = this.wrist == null ? base : Math.round((base + direction * WRIST_STEP) / WRIST_STEP) * WRIST_STEP;
      next = Math.min(WRIST_MAX, Math.max(WRIST_MIN, next));
      this.applyWrist(String(next), focusSelector);
    }

    facetBodyMarkup(facet) {
      if (facet.type === 'price') return this.priceMarkup();
      if (facet.type === 'wrist') return this.wristMarkup();
      const options = this.facetOptions(facet);
      const search =
        facet.searchable && options.length > 8
          ? `<input type="search" class="tnws-shop-options__search" placeholder="Find a ${facet.label.toLowerCase()}" aria-label="Find a ${facet.label.toLowerCase()}" data-tnws-shop-option-search>`
          : '';
      return `${search}<div class="tnws-shop-options${options.length > 10 ? ' is-long' : ''}">${options
        .map((o) => this.optionMarkup(facet, o))
        .join('')}</div>`;
    }

    visibleFacets() {
      return FACETS.filter((facet) => {
        if (facet.type === 'price') return (this.pool || []).some((i) => i.isProduct);
        if (facet.type === 'wrist') return (this.pool || []).some((i) => i.maxWrist != null);
        return this.facetOptions(facet).length > 0;
      });
    }

    renderFacets() {
      if (!this.facetsEl || !this.pool) return;
      const scrollTop = this.facetsEl.scrollTop;
      this.facetsEl.innerHTML = this.visibleFacets()
        .map((facet) => {
          const active = this.facetActiveCount(facet);
          const open = this.openFacets.has(facet.key) || active;
          return `
          <details class="tnws-shop-facet" data-tnws-shop-facet-group="${facet.key}"${open ? ' open' : ''}>
            <summary class="tnws-shop-facet__summary">
              <span>${facet.label}</span>
              ${active ? `<span class="tnws-shop-facet__active">${active}</span>` : ''}
            </summary>
            <div class="tnws-shop-facet__body">${this.facetBodyMarkup(facet)}</div>
          </details>`;
        })
        .join('');
      this.facetsEl.scrollTop = scrollTop;
    }

    priceActive() {
      return Boolean(this.price.preset || this.price.min != null || this.price.max != null);
    }

    facetActiveCount(facet) {
      if (facet.type === 'price') return this.priceActive() ? 1 : 0;
      if (facet.type === 'wrist') return this.wrist != null ? 1 : 0;
      return this.selected.get(facet.key).size;
    }

    wristChipLabel() {
      return `Fits ${formatWrist(this.wrist)} wrist`;
    }

    pillLabel(facet) {
      if (facet.type === 'price') {
        if (!this.priceActive()) return facet.label;
        return this.priceChipLabel();
      }
      if (facet.type === 'wrist') return this.wrist != null ? this.wristChipLabel() : facet.label;
      const set = this.selected.get(facet.key);
      if (!set.size) return facet.label;
      if (set.size === 1) return [...set][0];
      return `${facet.label} (${set.size})`;
    }

    renderPills() {
      if (!this.pills) return;
      const facets = this.pool ? this.visibleFacets().filter((f) => f.primary) : FACETS.filter((f) => f.primary);
      this.pills.innerHTML = facets
        .map((facet) => {
          const active = this.facetActiveCount(facet) > 0;
          return `<button type="button" class="tnws-shop-pill${active ? ' is-active' : ''}" data-tnws-shop-pill="${facet.key}" aria-expanded="${this.popoverKey === facet.key}">
            <span>${escapeHtml(this.pillLabel(facet))}</span>
            <span class="tnws-shop-pill__caret" aria-hidden="true"></span>
          </button>`;
        })
        .join('');
    }

    priceChipLabel() {
      if (this.price.preset) return PRICE_PRESETS.find((p) => p.id === this.price.preset)?.label || 'Price';
      const { min, max } = this.price;
      if (min != null && max != null) return `${formatMoney(min)} – ${formatMoney(max)}`;
      if (min != null) return `${formatMoney(min)} & up`;
      if (max != null) return `Under ${formatMoney(max)}`;
      return 'Price';
    }

    renderChips() {
      if (!this.chips) return;
      const chips = [];
      if (this.query) chips.push({ type: 'query', label: `“${this.query}”` });
      FACETS.forEach((facet) => {
        if (facet.type === 'price') {
          if (this.priceActive()) chips.push({ type: 'price', label: this.priceChipLabel() });
          return;
        }
        if (facet.type === 'wrist') {
          if (this.wrist != null) chips.push({ type: 'wrist', label: this.wristChipLabel() });
          return;
        }
        this.selected.get(facet.key).forEach((value) => chips.push({ type: facet.key, value, label: value }));
      });
      this.chips.hidden = chips.length === 0;
      this.chips.innerHTML =
        chips
          .map(
            (chip) => `<button type="button" class="tnws-shop-chip" data-tnws-shop-chip="${chip.type}" data-value="${escapeHtml(chip.value || '')}" aria-label="Remove ${escapeHtml(chip.label)}">
              <span>${escapeHtml(chip.label)}</span><span class="tnws-shop-chip__x" aria-hidden="true">×</span>
            </button>`
          )
          .join('') + (chips.length > 1 ? '<button type="button" class="tnws-shop-chips__clear" data-tnws-shop-clear>Clear all</button>' : '');

      const count = this.activeCount();
      if (this.badge) {
        this.badge.hidden = count === 0;
        this.badge.textContent = String(count);
      }
    }

    updateCount(count) {
      if (this.countEl) this.countEl.textContent = `${count} ${count === 1 ? this.noun.replace(/es$|s$/, '') : this.noun}`;
      if (this.applyButton) this.applyButton.textContent = count === 1 ? `Show 1 ${this.noun.replace(/es$|s$/, '')}` : `Show ${count} ${this.noun}`;
    }

    renderPagination(totalPages) {
      if (totalPages <= 1) {
        this.pagesEl.hidden = true;
        this.pagesEl.innerHTML = '';
        return;
      }
      const page = this.page;
      const parts = [];
      const push = (n) => parts.push(n);
      const windowSize = 1;
      for (let n = 1; n <= totalPages; n += 1) {
        if (n === 1 || n === totalPages || Math.abs(n - page) <= windowSize) push(n);
        else if (parts[parts.length - 1] !== '…') push('…');
      }
      const caret = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
      const items = parts
        .map((part) => {
          if (part === '…') return '<li><span class="pagination__item">…</span></li>';
          if (part === page) {
            return `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${part}">${part}</a></li>`;
          }
          return `<li><button type="button" class="pagination__item link" data-tnws-shop-page="${part}" aria-label="Page ${part}">${part}</button></li>`;
        })
        .join('');
      const prev =
        page > 1
          ? `<li><button type="button" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-tnws-shop-page="${page - 1}" aria-label="Previous page"><span class="svg-wrapper">${caret}</span></button></li>`
          : '';
      const next =
        page < totalPages
          ? `<li><button type="button" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-tnws-shop-page="${page + 1}" aria-label="Next page"><span class="svg-wrapper">${caret}</span></button></li>`
          : '';
      this.pagesEl.innerHTML = `<div class="pagination-wrapper"><nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">${prev}${items}${next}</ul></nav></div>`;
      this.pagesEl.hidden = false;
    }

    render({ scroll = true, refreshFacets = true } = {}) {
      if (!this.pool) return;
      this.clientMode = true;
      const results = this.filtered();
      const perPage = this.perPage === Infinity ? Math.max(results.length, 1) : this.perPage;
      const totalPages = Math.max(1, Math.ceil(results.length / perPage));
      this.page = Math.min(Math.max(1, this.page), totalPages);
      const slice = results.slice((this.page - 1) * perPage, this.page * perPage);

      this.grid.replaceChildren(...slice.map((item) => item.el));
      if (this.nativePagination) this.nativePagination.hidden = true;
      this.renderPagination(totalPages);
      if (this.emptyState) this.emptyState.hidden = results.length > 0;

      const productCount = results.filter((item) => item.isProduct).length;
      this.updateCount(productCount);
      this.renderChips();
      this.renderPills();
      if (refreshFacets) this.renderFacets();
      if (this.popoverKey) this.renderPopover();
      this.writeUrlState();
      this.syncFavorites();

      if (scroll) {
        const top = this.toolbar.getBoundingClientRect().top + window.scrollY - 90;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
    }

    syncFavorites() {
      try {
        if (typeof TnwsFavorites !== 'undefined') TnwsFavorites.syncButtons();
      } catch (e) {
        /* favorites script not on page */
      }
    }

    /* ---------- popover (desktop quick filters) ---------- */

    openPopover(key, anchor) {
      if (this.popoverKey === key) {
        this.closePopover();
        return;
      }
      this.popoverKey = key;
      this.popoverAnchor = anchor;
      this.renderPopover();
      this.popover.hidden = false;
      this.positionPopover();
      this.renderPills();
      this.popover.querySelector('[data-tnws-shop-option-search], input')?.focus({ preventScroll: true });
    }

    renderPopover() {
      const facet = FACETS.find((f) => f.key === this.popoverKey);
      if (!facet || !this.pool) return;
      const search = this.popover.querySelector('[data-tnws-shop-option-search]');
      const searchValue = search?.value || '';
      this.popover.innerHTML = `
        <div class="tnws-shop-popover__body">${this.facetBodyMarkup(facet)}</div>
        <div class="tnws-shop-popover__footer">
          <button type="button" class="tnws-shop-popover__clear" data-tnws-shop-clear-facet="${facet.key}">Clear</button>
          <button type="button" class="tnws-shop-popover__done" data-tnws-shop-popover-close>Done</button>
        </div>`;
      const newSearch = this.popover.querySelector('[data-tnws-shop-option-search]');
      if (newSearch && searchValue) {
        newSearch.value = searchValue;
        this.filterOptionList(newSearch);
      }
    }

    positionPopover() {
      if (this.popover.hidden || !this.popoverAnchor) return;
      const rect = this.popoverAnchor.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 16);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      this.popover.style.top = `${rect.bottom + 8}px`;
      this.popover.style.left = `${left}px`;
      this.popover.style.width = `${width}px`;
    }

    closePopover() {
      if (!this.popoverKey) return;
      this.popoverKey = null;
      this.popoverAnchor = null;
      this.popover.hidden = true;
      this.renderPills();
    }

    filterOptionList(input) {
      const term = input.value.trim().toLowerCase();
      input.parentElement.querySelectorAll('.tnws-shop-option').forEach((option) => {
        const label = option.querySelector('.tnws-shop-option__label')?.textContent.toLowerCase() || '';
        option.hidden = Boolean(term) && !label.includes(term);
      });
    }

    /* ---------- drawer ---------- */

    openDrawer() {
      this.closePopover();
      this.renderFacets();
      this.drawer.hidden = false;
      document.body.classList.add('tnws-shop-drawer-open');
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      this.drawer.querySelector('.tnws-shop-drawer__close')?.focus({ preventScroll: true });
    }

    closeDrawer() {
      if (this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('tnws-shop-drawer-open');
      window.setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
      }, 280);
      this.toolbar.querySelector('[data-tnws-shop-open]')?.focus({ preventScroll: true });
    }

    /* ---------- events ---------- */

    onChange(options = {}) {
      this.page = 1;
      this.render(options);
    }

    handleInput(event) {
      const target = event.target;
      if (target.matches('[data-tnws-shop-facet]')) {
        const set = this.selected.get(target.dataset.tnwsShopFacet);
        if (target.checked) set.add(target.value);
        else set.delete(target.value);
        this.onChange({ scroll: false });
        return true;
      }
      if (target.matches('[data-tnws-shop-price-preset]')) {
        const preset = PRICE_PRESETS.find((p) => p.id === target.value);
        if (preset) this.price = { preset: preset.id, min: preset.min, max: preset.max };
        this.onChange({ scroll: false });
        return true;
      }
      return false;
    }

    handlePriceRange(scope) {
      const minInput = scope.querySelector('[data-tnws-shop-price-min]');
      const maxInput = scope.querySelector('[data-tnws-shop-price-max]');
      const parse = (input) => {
        const v = input?.value.trim();
        if (!v) return null;
        const n = Number(v);
        return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
      };
      this.price = { preset: '', min: parse(minInput), max: parse(maxInput) };
      this.page = 1;
      this.render({ scroll: false, refreshFacets: false });
      this.renderFacetsExceptPrice();
    }

    renderFacetsExceptPrice() {
      if (!this.facetsEl) return;
      this.facetsEl.querySelectorAll('[data-tnws-shop-facet-group]').forEach((group) => {
        const facet = FACETS.find((f) => f.key === group.dataset.tnwsShopFacetGroup);
        if (!facet || facet.type === 'price') return;
        const body = group.querySelector('.tnws-shop-facet__body');
        if (body) body.innerHTML = this.facetBodyMarkup(facet);
      });
    }

    clearAll() {
      this.selected.forEach((set) => set.clear());
      this.price = { preset: '', min: null, max: null };
      this.setWrist(null);
      this.query = '';
      if (this.queryInput) this.queryInput.value = '';
      this.onChange({ scroll: false });
    }

    removeChip(chip) {
      const type = chip.dataset.tnwsShopChip;
      if (type === 'query') {
        this.query = '';
        if (this.queryInput) this.queryInput.value = '';
      } else if (type === 'price') {
        this.price = { preset: '', min: null, max: null };
      } else if (type === 'wrist') {
        this.setWrist(null);
      } else {
        this.selected.get(type)?.delete(chip.dataset.value);
      }
      this.onChange({ scroll: false });
    }

    async changeSort(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      window.history.replaceState(window.history.state, '', url.toString());
      this.grid.classList.add('is-tnws-busy');
      try {
        this.pool = await this.fetchPool(value);
        this.page = 1;
        this.render({ scroll: false });
      } catch (error) {
        window.location.href = url.toString();
      } finally {
        this.grid.classList.remove('is-tnws-busy');
      }
    }

    bind() {
      this.toolbar.addEventListener('click', (event) => {
        const pill = event.target.closest('[data-tnws-shop-pill]');
        if (pill) {
          event.stopPropagation();
          if (!this.pool) return;
          this.openPopover(pill.dataset.tnwsShopPill, pill);
          return;
        }
        if (event.target.closest('[data-tnws-shop-open]')) {
          this.openDrawer();
          return;
        }
        const chip = event.target.closest('[data-tnws-shop-chip]');
        if (chip) {
          this.removeChip(chip);
          return;
        }
        if (event.target.closest('[data-tnws-shop-clear]')) this.clearAll();
      });

      this.perPageSelect?.addEventListener('change', () => {
        const value = this.perPageSelect.value === 'all' ? Infinity : Number(this.perPageSelect.value);
        this.perPage = value;
        this.storePerPage(value);
        this.onChange({ scroll: false });
      });

      this.sortSelect?.addEventListener('change', () => this.changeSort(this.sortSelect.value));

      const panelHandlers = (panel) => {
        panel.addEventListener('click', (event) => {
          const step = event.target.closest('[data-tnws-shop-wrist-step]');
          if (step) {
            const direction = Number(step.dataset.tnwsShopWristStep);
            this.stepWrist(direction, `[data-tnws-shop-wrist-step="${direction}"]`);
            return;
          }
          const preset = event.target.closest('[data-tnws-shop-wrist-preset]');
          if (preset) {
            const size = Number(preset.dataset.tnwsShopWristPreset);
            this.applyWrist(this.wrist === size ? '' : String(size), `[data-tnws-shop-wrist-preset="${size}"]`);
          }
        });
        const wristRaw = (input) => (input.matches('[data-tnws-shop-wrist-cm]') && input.value.trim() ? `${input.value}cm` : input.value);
        panel.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || !event.target.matches('[data-tnws-shop-wrist-input], [data-tnws-shop-wrist-cm]')) return;
          event.preventDefault();
          const attr = event.target.matches('[data-tnws-shop-wrist-cm]') ? 'data-tnws-shop-wrist-cm' : 'data-tnws-shop-wrist-input';
          this.applyWrist(wristRaw(event.target), `[${attr}]`);
        });
        panel.addEventListener('input', (event) => {
          const input = event.target;
          if (!input.matches('[data-tnws-shop-wrist-input], [data-tnws-shop-wrist-cm]')) return;
          const isCm = input.matches('[data-tnws-shop-wrist-cm]');
          const other = input.closest('.tnws-shop-wrist')?.querySelector(isCm ? '[data-tnws-shop-wrist-input]' : '[data-tnws-shop-wrist-cm]');
          const n = parseFloat(input.value.replace(',', '.'));
          if (!other) return;
          if (!Number.isFinite(n) || n <= 0) other.value = '';
          else other.value = isCm ? Number((n / 2.54).toFixed(2)) : Number((n * 2.54).toFixed(1));
        });
        panel.addEventListener('change', (event) => {
          if (event.target.matches('[data-tnws-shop-wrist-input], [data-tnws-shop-wrist-cm]')) {
            this.applyWrist(wristRaw(event.target));
            return;
          }
          if (this.handleInput(event)) return;
          if (event.target.matches('[data-tnws-shop-price-min], [data-tnws-shop-price-max]')) {
            this.handlePriceRange(event.target.closest('.tnws-shop-price-range'));
          }
        });
        panel.addEventListener('input', (event) => {
          if (event.target.matches('[data-tnws-shop-option-search]')) this.filterOptionList(event.target);
        });
        panel.addEventListener('toggle', (event) => {
          const group = event.target.closest?.('[data-tnws-shop-facet-group]');
          if (!group) return;
          if (group.open) this.openFacets.add(group.dataset.tnwsShopFacetGroup);
          else this.openFacets.delete(group.dataset.tnwsShopFacetGroup);
        }, true);
      };

      if (this.popover) {
        panelHandlers(this.popover);
        this.popover.addEventListener('click', (event) => {
          event.stopPropagation();
          if (event.target.closest('[data-tnws-shop-popover-close]')) this.closePopover();
          const clear = event.target.closest('[data-tnws-shop-clear-facet]');
          if (clear) {
            const key = clear.dataset.tnwsShopClearFacet;
            if (key === 'price') this.price = { preset: '', min: null, max: null };
            else if (key === 'wrist') this.setWrist(null);
            else this.selected.get(key)?.clear();
            this.onChange({ scroll: false });
          }
        });
      }

      if (this.drawer) {
        panelHandlers(this.drawer);
        this.drawer.addEventListener('click', (event) => {
          if (event.target.closest('[data-tnws-shop-close]')) this.closeDrawer();
          else if (event.target.closest('[data-tnws-shop-clear]')) this.clearAll();
        });
        let queryTimer;
        this.queryInput?.addEventListener('input', () => {
          window.clearTimeout(queryTimer);
          queryTimer = window.setTimeout(() => {
            this.query = this.queryInput.value.trim();
            this.onChange({ scroll: false });
          }, 200);
        });
      }

      this.pagesEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-tnws-shop-page]');
        if (!button) return;
        this.page = Number(button.dataset.tnwsShopPage);
        this.render({ refreshFacets: false });
      });

      document.addEventListener('click', (event) => {
        if (this.popoverKey && !event.target.closest('[data-tnws-shop-popover]')) this.closePopover();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closePopover();
        this.closeDrawer();
      });
      window.addEventListener('resize', () => this.positionPopover());
      window.addEventListener('scroll', () => this.positionPopover(), { passive: true });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-tnws-shop]').forEach((root) => {
      if (root.dataset.tnwsShopReady) return;
      root.dataset.tnwsShopReady = 'true';
      new TnwsShopFilters(root);
    });
  };

  if (document.readyState === 'complete') init();
  else {
    document.addEventListener('DOMContentLoaded', init);
    window.addEventListener('load', init);
  }
})();
